import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './Header.css';
import axios from 'axios';
import { Card, Typography, Button, Form, Input, Space } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faPenToSquare, faMessage, faSignOutAlt, faCrown, faUserTie, faChalkboardTeacher, faBuilding, faBook, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import LanguageSwitcher from '../LanguageSwitcher'; 
import { useTranslation } from 'react-i18next';
import UserManual from '../../Pages/UserManual/UserManual';

const { Title, Text } = Typography;

const Header = ({ onSignOut }) => {
  const [username, setUsername] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const [profile, setProfile] = useState(null);
  const [showProfile, setShowProfile] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const API_URL = process.env.REACT_APP_API_URL;
  const { t } = useTranslation();

  // Auth pages we never want the Back button to return to.
  const isAuthRoute = (path) =>
    ['/', '/signin', '/forgot-password', '/change-password'].includes(
      (path || '').split('?')[0]
    );

  // Track the actual in-app route history so the Back button returns to the
  // real previous page (browser history can include non-app entries that make
  // navigate(-1) unreliable in a HashRouter app). Login/auth pages are never
  // recorded, so Back can never land back on the sign-in page.
  useEffect(() => {
    if (isAuthRoute(location.pathname)) return;
    const current = location.pathname + location.search;
    const stack = JSON.parse(sessionStorage.getItem('navStack') || '[]');
    if (stack[stack.length - 1] !== current) {
      stack.push(current);
      if (stack.length > 50) stack.shift();
      sessionStorage.setItem('navStack', JSON.stringify(stack));
    }
  }, [location]);

  useEffect(() => {
    const fetchUserProfile = async () => {
      const token = localStorage.getItem('token');
      try {
        const response = await axios.get(`${API_URL}/users/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUsername(response.data.firstname);
        setProfile(response.data);

        // Fetch unread count
        const unreadCountResponse = await axios.get(`${API_URL}/chat/unread-count/${response.data.userid}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUnreadCount(unreadCountResponse.data.unreadCount);
      } catch (error) {
        console.error('Error fetching user profile or unread count:', error);
      }
    };

    fetchUserProfile();
    const interval = setInterval(fetchUserProfile, 60000); // Refresh every minute

    return () => clearInterval(interval);
  }, [API_URL]);

  const handleBackClick = () => {
    const stack = JSON.parse(sessionStorage.getItem('navStack') || '[]');
    // Drop the current page, then go to the previous distinct route.
    stack.pop();
    const prev = stack[stack.length - 1];
    sessionStorage.setItem('navStack', JSON.stringify(stack));
    // Never fall back to the browser's history (it can lead to the login page);
    // go to the dashboard when there's no earlier in-app page.
    if (prev && !isAuthRoute(prev)) {
      navigate(prev);
    } else {
      navigate('/dashboard');
    }
  };

  const handleChatClick = () => {
    navigate('/chat');
  };

  const handleUserManualClick = () => {
    navigate('/user-manual');
  };

  const handleSignOut = async () => {
    try {
      await axios.get(`${API_URL}/auth/signout`);
      localStorage.removeItem('token');
      if (typeof onSignOut === 'function') {
        onSignOut();
      }
      window.location.href = '/signin';
    } catch (error) {
      console.error('Error signing out:', error);
      alert('Failed to sign out. Please try again.');
    }
  };

  const toggleProfile = () => {
    setShowProfile(!showProfile);
  };

  const toggleChangePassword = () => {
    setShowChangePassword(!showChangePassword);
  };

  const handleDoodleClick = () => {
    navigate('/doodle');
  };

  const handleChangePassword = async ({ password, newPassword, confirmNewPassword }) => {
    if (newPassword !== confirmNewPassword) {
      alert("New passwords do not match");
      return;
    }

    const token = localStorage.getItem('token');
    try {
      const response = await axios.post(`${API_URL}/users/change-password`, {
        password,
        newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert(response.data.message);
      setShowChangePassword(false);
      handleSignOut();
    } catch (error) {
      console.error('Error changing password:', error);
      alert('Failed to change password. Please try again.');
    }
  };

  return (
    <div className="modern-header">
      <div className="header-container">
        <div className="logo-section">
          <div className="logo-container">
            <img src='/images/keaes_hd.png' alt="KEAES Logo" className="modern-logo" />
          </div>
        </div> 
          <div className="header-actions">
            <button onClick={handleBackClick} className="modern-button back-button" title={t('back')}>
              <FontAwesomeIcon icon={faArrowLeft} className="header-icon" />
              <span className="button-text">{t('back')}</span>
            </button>

            <div className="user-profile" onClick={toggleProfile}>
                <FontAwesomeIcon icon={faUser} className="header-icon user-icon" />
                <div style={{ display: 'flex', flexDirection: 'column', marginLeft: '8px' }}>
                  <span className="username-text">
                    {t('hello')} <strong>{username}</strong>!
                  </span>
                </div>
              </div>

              {/* Role & Branch Info - enhanced design */}
              {profile && (
                <div className="role-branch-container">
                  <div className={`role-badge role-${profile.role.toLowerCase()}`}>
                    <FontAwesomeIcon 
                      icon={
                        profile.role === 'superadmin' ? faCrown : 
                        profile.role === 'admin' ? faUserTie : 
                        faChalkboardTeacher
                      } 
                      className="role-icon" 
                    />
                    {profile.role}
                  </div>

                  {profile.role !== 'superadmin' && profile.branch_name && (
                    <div className="branch-badge">
                      <FontAwesomeIcon icon={faBuilding} className="branch-icon" />
                      {profile.branch_name}
                    </div>
                  )}
                </div>
              )}
        
            {(profile?.role === 'superadmin' || profile?.role === 'admin' || profile?.role === 'teacher') && (
              <button onClick={handleChatClick} className="modern-button chat-button">
                <FontAwesomeIcon icon={faMessage} className="header-icon" />
                {unreadCount > 0 && <span className="notification-indicator">{unreadCount}</span>}
              </button>
            )}
            
            <button onClick={handleDoodleClick} className="modern-button doodle-button">
              <FontAwesomeIcon icon={faPenToSquare} className="header-icon" />
            </button>

            <button onClick={handleUserManualClick} className="modern-button manual-button">
              <FontAwesomeIcon icon={faBook} className="header-icon" />
            </button>
            
            <button onClick={handleSignOut} className="modern-button signout-button">
              <FontAwesomeIcon icon={faSignOutAlt} className="header-icon" />
              <span className="button-text">{t('sign out')}</span>
            </button>
            
            <div className="language-control">
              <LanguageSwitcher />
            </div>
          </div>
        </div>
        
        {showProfile && profile && (
          <div className={`profile-popup ${showProfile ? 'show' : ''}`} style={{ position: 'fixed', top: 70, right: 24, zIndex: 1000 }}>
            <Card
              style={{
                width: 320,
                borderRadius: 16,
                boxShadow: '0 8px 32px rgba(125, 220, 192, 0.2), 0 4px 16px rgba(0, 0, 0, 0.1)',
                padding: '0 0 16px 0',
                border: '1px solid rgba(125, 220, 192, 0.2)',
                background: 'white'
              }}
              bodyStyle={{ paddingTop: 0 }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '20px 20px 12px',
                  borderBottom: '1px solid rgba(125, 220, 192, 0.15)',
                  background: 'linear-gradient(135deg, rgba(125, 220, 192, 0.05) 0%, rgba(244, 248, 187, 0.05) 100%)'
                }}
              >
                <Title level={5} style={{ margin: 0, color: '#2d3748', fontWeight: 600 }}>
                  User Profile
                </Title>
                <Button
                  type="text"
                  icon={<CloseOutlined />}
                  onClick={toggleProfile}
                  style={{ 
                    marginRight: -8,
                    color: '#718096',
                    borderRadius: '8px'
                  }}
                />
              </div>

              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Title level={4} style={{ marginBottom: 8, color: '#2d3748', fontWeight: 700 }}>
                  {profile.firstname} {profile.lastname}
                </Title>
                <Text style={{ 
                  color: '#7DDCC0', 
                  fontSize: '14px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  {profile.role}
                </Text>

                <Button
                  type="link"
                  onClick={toggleChangePassword}
                  style={{ 
                    marginTop: 20, 
                    color: '#5A9D8A', 
                    fontWeight: 600,
                    fontSize: '14px',
                    padding: '8px 16px',
                    borderRadius: '8px',
                    background: 'rgba(125, 220, 192, 0.1)',
                    border: '1px solid rgba(125, 220, 192, 0.2)'
                  }}
                >
                  Change My Password
                </Button>
              </div>

              {showChangePassword && (
                <div style={{ 
                  padding: '0 20px 8px',
                  borderTop: '1px solid rgba(125, 220, 192, 0.15)',
                  paddingTop: '20px'
                }}>
                  <Form layout="vertical" onFinish={handleChangePassword}>
                    <Form.Item 
                      label={<span style={{ color: '#2d3748', fontWeight: 600 }}>Current Password</span>} 
                      name="password" 
                      rules={[{ required: true }]}
                    >
                      <Input.Password style={{
                        borderRadius: '8px',
                        border: '1px solid rgba(125, 220, 192, 0.3)',
                        padding: '8px 12px'
                      }} />
                    </Form.Item>
                    <Form.Item 
                      label={<span style={{ color: '#2d3748', fontWeight: 600 }}>New Password</span>} 
                      name="newPassword" 
                      rules={[{ required: true }]}
                    >
                      <Input.Password style={{
                        borderRadius: '8px',
                        border: '1px solid rgba(125, 220, 192, 0.3)',
                        padding: '8px 12px'
                      }} />
                    </Form.Item>
                    <Form.Item 
                      label={<span style={{ color: '#2d3748', fontWeight: 600 }}>Confirm New Password</span>} 
                      name="confirmNewPassword" 
                      rules={[{ required: true }]}
                    >
                      <Input.Password style={{
                        borderRadius: '8px',
                        border: '1px solid rgba(125, 220, 192, 0.3)',
                        padding: '8px 12px'
                      }} />
                    </Form.Item>
                    <Space style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
                      <Button 
                        type="primary" 
                        htmlType="submit"
                        style={{
                          background: 'linear-gradient(135deg, #7DDCC0 0%, #5A9D8A 100%)',
                          border: 'none',
                          borderRadius: '8px',
                          fontWeight: 600,
                          boxShadow: '0 2px 8px rgba(125, 220, 192, 0.3)'
                        }}
                      >
                        Change Password
                      </Button>
                      <Button 
                        onClick={toggleChangePassword}
                        style={{
                          borderRadius: '8px',
                          border: '1px solid rgba(125, 220, 192, 0.3)',
                          color: '#718096'
                        }}
                      >
                        Cancel
                      </Button>
                    </Space>
                  </Form>
                </div>
              )}
            </Card>
          </div>
        )}
      </div>
  );
};

export default Header;